import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Phone, Star, BadgeCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { scrollToId } from "./Nav";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/contact";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1730910260035-626b5e8458fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMG9yYW5nZSUyMGZpYmVyJTIwbmV0d29ya3xlbnwwfHx8fDE3ODc5MjExMjZ8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ text, delay, accent }) => (
  <div className="overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block font-display font-medium tracking-tighter leading-[0.95] text-5xl md:text-7xl lg:text-[6.5vw] ${
        accent ? "text-[#FF7900]" : "text-white"
      }`}
    >
      {text}
    </motion.span>
  </div>
);

export const Hero = () => {
  const { lang, t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 160]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.3]);
  const [google, setGoogle] = useState(null);

  useEffect(() => {
    api.get("/google-rating").then((r) => {
      if (r.data.rating > 0) setGoogle(r.data);
    }).catch(() => {});
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, opacity }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-[#050505]/20" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 w-full pt-40 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FF7900] mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>

        <h1 data-testid="hero-heading">
          <MaskedLine text={t.hero.line1} delay={0.25} />
          <MaskedLine text={t.hero.line2} delay={0.4} />
          <MaskedLine text={t.hero.line3} delay={0.55} accent />
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85 }}
            className="max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("contact")}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#FF7900] hover:bg-white hover:text-black text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300"
            >
              {t.hero.cta1}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("services")}
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-3 border border-white/25 hover:border-[#FF7900] hover:text-[#FF7900] text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300"
            >
              {t.hero.cta2}
              <ArrowDown className="w-4 h-4" />
            </button>
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-3 text-white/80 hover:text-[#FF7900] font-semibold px-2 py-4 transition-colors duration-300"
            >
              <span className="w-10 h-10 rounded-full border border-white/25 flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </span>
              {PHONE_DISPLAY}
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 pt-8 border-t border-white/10 grid grid-cols-3 gap-6"
          data-testid="hero-stats"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i}>
              <p className="font-display text-3xl md:text-5xl font-medium text-white">
                {s.v}
              </p>
              <p className="mt-1 text-xs md:text-sm uppercase tracking-widest text-white/50">{s.l}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-8 flex flex-wrap items-center gap-3"
          data-testid="hero-trust-bar"
        >
          {google && (
            <a
              href={google.url || undefined}
              target={google.url ? "_blank" : undefined}
              rel="noreferrer"
              data-testid="hero-google-badge"
              className={`inline-flex items-center gap-2.5 border border-white/15 bg-white/[0.04] backdrop-blur-sm px-4 py-2 rounded-full text-sm ${google.url ? "hover:border-[#FF7900] transition-colors duration-300" : "cursor-default"}`}
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
              </svg>
              <span className="font-semibold">{String(google.rating).replace(".", lang === "fr" ? "," : ".")}/5</span>
              <Star className="w-3.5 h-3.5 text-[#FBBC04] fill-[#FBBC04]" />
              {google.count > 0 && (
                <span className="text-white/50">
                  {google.count} {t.hero.trustReviews}
                </span>
              )}
            </a>
          )}
          <span
            data-testid="hero-partner-chip"
            className="inline-flex items-center gap-2 border border-white/15 bg-white/[0.04] backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white/70"
          >
            <BadgeCheck className="w-4 h-4 text-[#FF7900]" />
            {t.hero.trustPartner}
          </span>
        </motion.div>
      </div>
    </section>
  );
};
